import React from 'react';

const INDENT = '  ';

// Controlled code textarea: Tab/Shift+Tab indentation, Enter auto-indent.
// setRangeText keeps native undo mostly intact; it does NOT fire onChange,
// so we propagate manually after each mutation.
// A11y: Esc then Tab leaves the field (Tab is only captured while focused).
export default function CodeTextarea({ value, onChange, className, placeholder, ...rest }) {
    const escapedRef = React.useRef(false);

    const handleKeyDown = (e) => {
        const ta = e.currentTarget;
        if (e.key === 'Escape') { escapedRef.current = true; return; }
        if (e.key === 'Tab' && escapedRef.current) { escapedRef.current = false; return; } // let focus move
        escapedRef.current = false;

        if (e.key === 'Tab') {
            e.preventDefault();
            const { selectionStart: s, selectionEnd: end, value: v } = ta;
            const lineStart = v.lastIndexOf('\n', s - 1) + 1;
            if (s !== end && v.slice(s, end).includes('\n')) {
                // Block indent/outdent across the selected lines
                const block = v.slice(lineStart, end);
                const next = e.shiftKey
                    ? block.replace(/^ {1,2}/gm, '')
                    : block.replace(/^/gm, INDENT);
                ta.setRangeText(next, lineStart, end, 'select');
            } else if (e.shiftKey) {
                const lead = v.slice(lineStart).match(/^ {1,2}/);
                if (lead) ta.setRangeText('', lineStart, lineStart + lead[0].length, 'end');
            } else {
                ta.setRangeText(INDENT, s, end, 'end');
            }
            onChange(ta.value);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const { selectionStart: s, selectionEnd: end, value: v } = ta;
            const lineStart = v.lastIndexOf('\n', s - 1) + 1;
            const indent = (v.slice(lineStart).match(/^[ \t]*/) || [''])[0];
            const prevChar = v.slice(0, s).trimEnd().slice(-1);
            const extra = prevChar === '{' || prevChar === '[' ? INDENT : '';
            ta.setRangeText('\n' + indent + extra, s, end, 'end');
            onChange(ta.value);
        }
    };

    return (
        <textarea
            value={value} placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            className={className}
            {...rest}
        />
    );
}

// Pretty-print JSON that contains Jinja spans. Masks {{ … }} / {% … %} as
// sentinel tokens (remembering original quoting), JSON.parse/stringify,
// restores. Any parse failure returns {ok:false} and the caller leaves the
// text untouched — formatting is best-effort, never destructive.
export function formatJinjaJson(src) {
    const spans = [];
    let masked = src.replace(/"?(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})"?/g, (m) => {
        const quoted = m.startsWith('"') && m.endsWith('"') && m.length > 2;
        spans.push({ text: quoted ? m.slice(1, -1) : m, quoted });
        return `"@@J${spans.length - 1}@@`;
    });
    let parsed;
    try { parsed = JSON.parse(masked); } catch (e) { return { ok: false, error: e.message }; }
    const pretty = JSON.stringify(parsed, null, 2).replace(/"@@J(\d+)@@"/g, (_, i) => {
        const { text, quoted } = spans[Number(i)];
        return quoted ? `"${text}"` : text;
    });
    return { ok: true, text: pretty };
}
